// ==========================================================================
// screens/Vehicles.tsx — every vehicle at a glance (FF-008, redesigned in the
// FF-026 pass 9 Oct 2026). One card per vehicle: this FY's work km as the
// headline, a 12-week bar strip, the 5,000 km cents-per-km line, trips to
// review, and the big "Log a trip".
// ==========================================================================
import QueryUnavailable from "@/components/QueryUnavailable";
import { Link } from "react-router-dom";
import { addDays, addWeeks, format, startOfWeek } from "date-fns";
import { Car, Plus } from "lucide-react";
import ChapterHeader from "@/components/ChapterHeader";
import ActivityExplorer from "@/components/ActivityExplorer";
import { ymd } from "@/lib/calendar";
import { SOLD_HOW, fyOf, typeOf, useTrips, useUsePeriods, useVehicles, vehicleStats, type Trip, type Vehicle } from "@/lib/vehicles";
import { money } from "@/lib/money";

export function Stat({ label, value, unit, tone = "" }: { label: string; value: string | number; unit?: string; tone?: string }) {
  return (
    <div>
      <div className={`num text-2xl font-semibold ${tone}`}>{typeof value === "number" ? value.toLocaleString("en-AU") : value}{unit && <span className="ml-1 text-sm font-normal text-ink-muted">{unit}</span>}</div>
      <div className="text-xs text-ink-muted">{label}</div>
    </div>
  );
}

export function weeklyKm(trips: Trip[], weeks = 12) {
  const start = startOfWeek(new Date(), { weekStartsOn: 1 });
  return Array.from({ length: weeks }, (_, i) => {
    const from = addWeeks(start, i - (weeks - 1)); const to = addDays(from, 6);
    const inW = trips.filter((t) => t.trip_date >= ymd(from) && t.trip_date <= ymd(to));
    const use = (t: Trip) => t.reviewed_use ?? t.proposed_use;
    return { label: format(from, "d/M"), value: inW.filter((t) => use(t) === "work").reduce((a, t) => a + t.distance_km, 0), value2: inW.filter((t) => use(t) !== "work").reduce((a, t) => a + t.distance_km, 0) };
  });
}

export default function Vehicles() {
  const vehicles = useVehicles(); const trips = useTrips(); const periods = useUsePeriods();
  const fy = fyOf(new Date());
  const active = (vehicles.data ?? []).filter((v) => !v.assets.disposed_on).sort((a, b) => Number(typeOf(b.vehicle_type).trips) - Number(typeOf(a.vehicle_type).trips) || a.assets.name.localeCompare(b.assets.name));
  const sold = (vehicles.data ?? []).filter((v) => v.assets.disposed_on).sort((a, b) => (b.assets.disposed_on ?? "").localeCompare(a.assets.disposed_on ?? ""));
  if (vehicles.isError) return <QueryUnavailable name="Vehicles" />;

  return (
    <>
      <ChapterHeader chapter="vehicles" title="Vehicles" subtitle={`Cars, bikes, boats and trailers: value, rego and costs. Trips and kilometres for ${fy}.`}
        action={<Link to="/vehicles/new" className="flex items-center gap-1.5 rounded-full bg-surface px-4 py-2 text-sm font-semibold shadow-soft ring-1 ring-line/70 hover:ring-primary/40"><Plus className="h-4 w-4" /> Add vehicle</Link>} />

      {vehicles.data?.length === 0 && (
        <div className="rounded-lg bg-surface p-8 text-center shadow-soft ring-1 ring-line/70">
          <Car className="mx-auto h-10 w-10 text-ink-muted" />
          <p className="mt-3 font-semibold">No vehicles yet</p>
          <p className="text-sm text-ink-muted">Add one to start logging trips.</p>
        </div>
      )}

      <div className="grid gap-6 xl:grid-cols-2">
        {active.map((v) => {
          if (!typeOf(v.vehicle_type).trips) return <ToyCard key={v.asset_id} v={v} />;
          const mine = (trips.data ?? []).filter((t) => t.vehicle_id === v.asset_id);
          const s = vehicleStats(v.asset_id, trips.data ?? [], periods.data ?? [], fy);
          const capPct = Math.min(100, (s.workKm / 5000) * 100);
          return (
            <article key={v.asset_id} className="overflow-hidden rounded-lg bg-surface shadow-soft ring-1 ring-line/70">
              <div className="flex items-start justify-between gap-4 bg-panel px-6 py-5 text-panel-ink">
                <Link to={`/vehicles/${v.asset_id}`} className="min-w-0">
                  <div className="font-heading text-2xl font-semibold"><span className="mr-2 font-body">{typeOf(v.vehicle_type).emoji}</span>{v.assets.name}</div>
                  <div className="mt-0.5 text-sm text-panel-muted">{[v.year, v.make_model, v.rego].filter(Boolean).join(" · ") || "Details to fill in"}</div>
                </Link>
                <span className={`shrink-0 rounded-full px-3 py-1 text-xs font-semibold ${s.workFrom ? "bg-good text-white" : "bg-panel-raised text-panel-muted"}`}>{s.workFrom ? "Work use" : "Personal"}</span>
              </div>
              <div className="space-y-5 p-6">
                <div className="flex flex-wrap items-end gap-x-8 gap-y-3">
                  <div>
                    <div className="num text-[40px] font-semibold leading-none text-primary">{s.workKm.toLocaleString("en-AU")}</div>
                    <div className="mt-1 text-sm text-ink-muted">work km this year</div>
                  </div>
                  <Stat label="private km" value={s.privateKm} />
                  <Stat label="trips" value={s.trips} />
                  {s.lastKm !== null && <Stat label="odometer" value={s.lastKm} />}
                </div>
                <ActivityExplorer points={weeklyKm(mine)} kind="trips" loading={trips.isPending} error={trips.isError}/>
                {s.workFrom && (
                  <div>
                    <div className="h-2 overflow-hidden rounded-full bg-surface-raised"><div className="h-full rounded-full bg-primary" style={{ width: `${capPct}%` }} /></div>
                    <p className="mt-1.5 text-xs text-ink-muted"><span className="num">{s.workKm.toLocaleString("en-AU")}</span> of the 5,000 km cents-per-km limit{capPct >= 100 ? " — past it, the logbook method will claim more" : ""}</p>
                  </div>
                )}
                <div className="flex items-center gap-3">
                  <Link to={`/vehicles/${v.asset_id}/trip`} className="flex-1 rounded-full bg-primary px-5 py-3 text-center font-semibold text-white shadow-soft hover:opacity-90">Log a trip</Link>
                  <Link to={`/vehicles/${v.asset_id}`} className="rounded-full px-5 py-3 font-semibold ring-1 ring-line hover:ring-primary/40">{s.toReview > 0 ? <span className="text-warn">{s.toReview} to review</span> : "Details"}</Link>
                </div>
              </div>
            </article>
          );
        })}
      </div>

      {sold.length > 0 && (
        <section className="mt-10">
          <h2 className="mb-3 text-[17px] font-semibold">Sold</h2>
          <ul className="overflow-hidden rounded-lg bg-surface shadow-soft ring-1 ring-line/70">
            {sold.map((v) => (
              <li key={v.asset_id} className="border-b border-line/60 last:border-0">
                <Link to={`/vehicles/${v.asset_id}`} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-5 py-3 hover:bg-surface-raised/60">
                  <span className="text-xl">{typeOf(v.vehicle_type).emoji}</span>
                  <span className="min-w-0 flex-1"><b>{v.assets.name}</b><span className="block text-xs text-ink-muted">Sold {format(new Date(v.assets.disposed_on + "T00:00"), "d MMM yyyy")}{v.assets.sold_how ? `, ${SOLD_HOW.find((h) => h.key === v.assets.sold_how)?.label.toLowerCase()}` : ""}</span></span>
                  <span className="num font-semibold">{v.assets.sold_price_cents !== null ? money(v.assets.sold_price_cents) : ""}</span>
                  <SaleMoneyBadge v={v} />
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
}

/** Where the sale money is up to. */
export function SaleMoneyBadge({ v }: { v: Vehicle }) {
  const a = v.assets;
  if (a.proceeds === "bank") return a.proceeds_transaction_id
    ? <span className="rounded-full bg-good/15 px-2.5 py-1 text-xs font-semibold text-good">Money in, matched</span>
    : <span className="rounded-full bg-warn/15 px-2.5 py-1 text-xs font-semibold text-warn">Waiting for the money{a.proceeds_expected_on ? `, ${format(new Date(a.proceeds_expected_on + "T00:00"), "d MMM")}` : ""}</span>;
  if (a.proceeds === "trade_in") return <span className="rounded-full bg-surface-raised px-2.5 py-1 text-xs">Traded in</span>;
  if (a.proceeds === "cash") return <span className="rounded-full bg-surface-raised px-2.5 py-1 text-xs">Cash</span>;
  return null;
}

/** A boat, trailer, jet ski or caravan: value, rego and finance, no trips. */
function ToyCard({ v }: { v: Vehicle }) {
  const t = typeOf(v.vehicle_type);
  return (
    <article className="overflow-hidden rounded-lg bg-surface shadow-soft ring-1 ring-line/70">
      <div className="flex items-start justify-between gap-4 bg-panel px-6 py-5 text-panel-ink">
        <Link to={`/vehicles/${v.asset_id}`} className="min-w-0">
          <div className="font-heading text-2xl font-semibold"><span className="mr-2 font-body">{t.emoji}</span>{v.assets.name}</div>
          <div className="mt-0.5 text-sm text-panel-muted">{[t.label, v.year, v.make_model, v.rego].filter(Boolean).join(" · ")}</div>
        </Link>
        <span className="shrink-0 rounded-full bg-panel-raised px-3 py-1 text-xs font-semibold text-panel-muted">{v.finance === "loan" ? "On a loan" : v.finance === "novated" ? "Leased" : "Owned"}</span>
      </div>
      <div className="flex flex-wrap items-end gap-x-8 gap-y-3 p-6">
        <div>
          <div className="num text-[40px] font-semibold leading-none text-primary">{v.value_cents !== null ? money(v.value_cents) : "—"}</div>
          <div className="mt-1 text-sm text-ink-muted">{v.value_as_at ? `worth, as at ${format(new Date(v.value_as_at + "T00:00"), "MMM yyyy")}` : "add a value estimate"}</div>
        </div>
        {v.assets.cost_cents !== null && <Stat label="paid" value={money(v.assets.cost_cents)} />}
        <Link to={`/vehicles/${v.asset_id}`} className="ml-auto rounded-full px-5 py-3 font-semibold ring-1 ring-line hover:ring-primary/40">Details</Link>
      </div>
    </article>
  );
}
